import {
  TypePermanentLocaleOverride,
  TypePermanentLocaleOverrideFields,
  TypeTemporaryLocaleOverride,
  TypeTemporaryLocaleOverrideFields,
} from '@/core/generated-types';

export interface LocaleOverrideStatus {
  entryName: string;
  active: boolean;
}

const hasStarted = (starts?: string) => {
  if (!starts) {
    return true;
  }

  const now = new Date();
  const startDate = new Date(starts);
  return now >= startDate;
};

const hasEnded = (ends?: string) => {
  if (!ends) {
    return false;
  }

  const now = new Date();
  const endDate = new Date(ends);
  return now >= endDate;
};

const getPermanentLocaleOverrideStatus = ({
  enabled,
}: TypePermanentLocaleOverrideFields) => {
  return enabled;
};

const getTemporaryLocaleOverrideStatus = ({
  enabled,
  starts,
  ends,
}: TypeTemporaryLocaleOverrideFields) => {
  if (!enabled) {
    return false;
  }

  return hasStarted(starts) && !hasEnded(ends);
};

export const getLocaleOverrideStatus = ({
  entryName,
  ...localeOverrideFields
}:
  | TypePermanentLocaleOverrideFields
  | TypeTemporaryLocaleOverrideFields): LocaleOverrideStatus => {
  let active = false;
  if ('starts' in localeOverrideFields && 'ends' in localeOverrideFields) {
    active = getTemporaryLocaleOverrideStatus(
      localeOverrideFields as TypeTemporaryLocaleOverrideFields,
    );
  } else {
    active = getPermanentLocaleOverrideStatus(
      localeOverrideFields as TypePermanentLocaleOverrideFields,
    );
  }

  return { entryName, active };
};

// When a locale override is found, the content should be hidden when the locale is disabled by the override.
export const shouldHideWithLocaleOverride = (
  localeOverride:
    | TypePermanentLocaleOverride
    | TypeTemporaryLocaleOverride
    | undefined,
) => {
  if (localeOverride) {
    const localeOverrideStatus = getLocaleOverrideStatus(localeOverride.fields);
    // Content should be hidden.
    if (!localeOverrideStatus.active) {
      return true;
    }
  }

  // Content should be displayed
  // because of regular Contentful localization
  // or because the override for this locale is enabled.
  return false;
};
